/* assets/product-badges.js
 * Owns exactly one thing: the two state attributes on [data-badges] inside each .grid-product.
 *
 * Contract:
 *   source of truth : .grid-product__link[href]  ->  { handle, variantId }
 *   data            : /products/<handle>.js      ->  vendor, tags, variants[]
 *   output          : data-discount="<n>"  +  data-extra-sale="0|1"   (CSS does the rest)
 *
 * It never creates, moves or removes DOM. Worst case on failure: the badges keep their
 * previous state. No shared state with the slider, no events, no awaits in common.
 */
(function () {
  if (window.__ProductBadgesBooted) return;
  window.__ProductBadgesBooted = true;

  // ===== CONFIG — mirrors the conditions of the two EXTRA SALE collections =====
  // A product qualifies when it is on sale AND matches one of these.
  const EXTRA_SALE_VENDORS = ["miss nori"];   // collection 263782334519
  const EXTRA_SALE_TAGS    = ["kids"];        // collection 166559088695

  // true  = "on sale" means ANY variant is discounted (matches how Shopify builds the collection)
  // false = only the currently selected variant counts (both badges then always agree)
  const EXTRA_SALE_ANY_VARIANT = true;

  const DEBUG = false;

  // ===== INTERNALS =====
  const CARD = ".grid-product";
  const LINK = ".grid-product__link";
  const HOLDER = "[data-badges]";

  const log = (...a) => { if (DEBUG) console.log("[Badges]", ...a); };
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));
  const norm = (s) => String(s || "").trim().toLowerCase();

  const VENDORS = EXTRA_SALE_VENDORS.map(norm);
  const TAGS    = EXTRA_SALE_TAGS.map(norm);

  const cache = new Map(); // handle -> Promise<json> while in flight, then json

  function parse(href) {
    if (!href) return null;
    const h = href.match(/\/products\/([^/?#]+)/i);
    if (!h) return null;
    const v = href.match(/[?&]variant=(\d+)/);
    return { handle: decodeURIComponent(h[1]), variantId: v ? v[1] : null };
  }

  function load(handle) {
    const hit = cache.get(handle);
    if (hit !== undefined) return hit; // json (sync) or Promise

    const p = fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: "same-origin" })
      .then((r) => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then((j) => { cache.set(handle, j); return j; }) // replace promise with value -> later reads are sync
      .catch((e) => { cache.delete(handle); throw e; });

    cache.set(handle, p);
    return p;
  }

  function isOnSale(variant) {
    return !!(variant &&
      Number.isFinite(Number(variant.compare_at_price)) &&
      Number(variant.compare_at_price) > Number(variant.price));
  }

  function pickVariant(json, variantId) {
    const variants = Array.isArray(json.variants) ? json.variants : [];
    if (!variants.length) return null;
    if (variantId) {
      const hit = variants.find((v) => String(v.id) === String(variantId));
      if (hit) return hit;
    }
    return variants.find((v) => v.available) || variants[0];
  }

  function stateFor(json, variantId) {
    const variant = pickVariant(json, variantId);

    // --- discount badge: always the selected variant ---
    let discount = 0;
    if (isOnSale(variant)) {
      // Liquid's divided_by on two integers floors — match it exactly
      const cap = Number(variant.compare_at_price);
      discount = Math.floor(((cap - Number(variant.price)) * 100) / cap);
    }

    // --- extra sale badge: the two collections' own conditions ---
    const variants = Array.isArray(json.variants) ? json.variants : [];
    const onSale = EXTRA_SALE_ANY_VARIANT ? variants.some(isOnSale) : isOnSale(variant);

    const vendorMatch = VENDORS.indexOf(norm(json.vendor)) !== -1;
    const tags = Array.isArray(json.tags) ? json.tags.map(norm) : [];
    const tagMatch = TAGS.some((t) => tags.indexOf(t) !== -1);

    const extra = onSale && (vendorMatch || tagMatch);

    return { discount, extra };
  }

  function paint(holder, st) {
    holder.setAttribute("data-discount", String(st.discount));
    holder.setAttribute("data-extra-sale", st.extra ? "1" : "0");
    if (st.discount > 0) {
      const el = holder.querySelector("[data-discount-value]");
      if (el) el.textContent = st.discount + "%";
    }
  }

  // Idempotent and convergent: safe to call from anywhere, any number of times, any order.
  function apply(card) {
    const holder = card.querySelector(HOLDER);
    if (!holder) return;

    const link = card.querySelector(LINK);
    const info = parse(link && link.getAttribute("href"));
    if (!info) return;

    const seq = (card.__badgeSeq = (card.__badgeSeq || 0) + 1);
    const got = load(info.handle);

    if (typeof got.then !== "function") {        // warm cache -> same tick, no flicker
      paint(holder, stateFor(got, info.variantId));
      return;
    }

    got.then((json) => {
      if (card.__badgeSeq !== seq) return;       // a newer swap already won
      paint(holder, stateFor(json, info.variantId));
    }).catch((e) => log("failed, keeping current state", info.handle, e));
  }

  // Prefetch every product this card can switch to, so apply() stays synchronous.
  function warm(card) {
    if (card.__badgeWarmed) return;
    card.__badgeWarmed = true;

    const seen = new Set();
    qsa("a[href*='/products/'], [data-url*='/products/'], [data-product-url*='/products/']", card)
      .forEach((el) => {
        const raw = el.getAttribute("href") ||
                    el.getAttribute("data-url") ||
                    el.getAttribute("data-product-url");
        const info = parse(raw);
        if (!info || seen.has(info.handle)) return;
        seen.add(info.handle);
        const r = load(info.handle);
        if (r && r.catch) r.catch(() => {});
      });

    log("warmed", seen.size, "products");
  }

  const wired = new WeakSet();

  function wire(card) {
    if (wired.has(card)) return;
    wired.add(card);

    const link = card.querySelector(LINK);
    if (!link) return;

    new MutationObserver(() => apply(card))
      .observe(link, { attributes: true, attributeFilter: ["href"] });

    card.addEventListener("pointerenter", () => warm(card), { passive: true, once: true });
    card.addEventListener("touchstart",   () => warm(card), { passive: true, once: true });
  }

  function boot() {
    qsa(CARD).forEach(wire);

    new MutationObserver((muts) => {
      muts.forEach((m) => m.addedNodes && m.addedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        if (n.matches && n.matches(CARD)) wire(n);
        else qsa(CARD, n).forEach(wire);
      }));
    }).observe(document.documentElement, { childList: true, subtree: true });

    window.ProductBadges = {
      apply,
      warm,
      refresh: (r) => qsa(CARD, r || document).forEach(wire),
      // debug helper: ProductBadges.explain(document.querySelector('.grid-product'))
      explain: (card) => {
        const info = parse(card.querySelector(LINK).getAttribute("href"));
        return Promise.resolve(load(info.handle)).then((j) => ({
          handle: info.handle,
          vendor: j.vendor,
          tags: j.tags,
          state: stateFor(j, info.variantId)
        }));
      }
    };
  }

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", boot, { passive: true })
    : boot();
})();